/**
 * What the Alert Flow draws: its nodes and the flows between them, laid out on the seven pipeline stages
 * (client change of 2026-10-30). Each column of the Sankey is one stage and carries that stage's name
 * (`src/lib/pipelineStages.ts`); a node sits in the column of the stage whose work produced it.
 *
 * The backend sends the counts per plane (candidates, verdicts, threats, remediations); this re-arranges them
 * by stage, so nothing here is computed again and the figures agree with the Pipeline and the ledger.
 *
 * Colours are design-token names, resolved by whoever draws them. Pure: no canvas, no clock.
 */
import { PIPELINE_STAGE_IDS, PIPELINE_STAGE_TITLES, type PipelineStageId } from './pipelineStages';

export interface AlertFlowCounts {
  raw: number;
  /** unique + duplicated: completed analysis past in-flight. */
  analyzed: number;
  /** anomalies + baseline: unique candidates past dedup. */
  unique: number;
  anomalies: number;
  baseline: number;
  threats: number;
  falsePositives: number;
  duplicated: number;
  pending: number;
  analyzing: number;
  errors: number;
  /** Open campaigns (two or more incidents each). */
  campaigns: number;
  /** Threats that belong to a campaign. */
  inCampaign: number;
  remediations: number;
  remPending: number;
  remConfirmed: number;
  remInProgress: number;
  remCompleted: number;
  remCancelled: number;
  remReverted: number;
  escalated: number;
  standalone: number;
}

export interface AlertFlowNode {
  /** Column 0 to 6: the index of the stage in the pipeline. */
  col: number;
  v: number;
  label: string;
  /** Design token that colours the node. */
  c: string;
  d: string;
  /** Terminal: nothing flows on from it (drawn with a mark on its edge). */
  t?: number;
}

export type AlertFlow = [from: string, to: string, value: number];

/** The seven columns, in order, each with the pipeline's own name for the stage. */
export const ALERT_FLOW_COLUMNS: { id: PipelineStageId; title: string }[] = PIPELINE_STAGE_IDS.map((id) => ({
  id,
  title: PIPELINE_STAGE_TITLES[id],
}));

/** Columns from this one on count remediations, not alerts: their percentages are of the remediations. */
export const FIRST_REMEDIATION_COLUMN = 5;

export function buildAlertFlowNodes(c: AlertFlowCounts): Record<string, AlertFlowNode> {
  const nodes: Record<string, AlertFlowNode> = {
    // 01 Ingest + Normalize
    raw: { col: 0, v: c.raw, label: 'Raw alerts', c: '--prog', d: 'Every registered threat-event candidate.' },
    // 02 Dedup
    unique: {
      col: 1,
      v: c.unique,
      label: 'Unique',
      c: '--prog',
      d: 'Non-duplicate completed candidates (anomalies + baseline normal).',
    },
    // 03 Cluster + Score
    ano: {
      col: 2,
      v: c.anomalies,
      label: 'Anomalies',
      c: '--prog',
      d: 'Candidates with outcome threat or false_positive (detectors / LLM path).',
    },
    base: {
      col: 2,
      v: c.baseline,
      label: 'Baseline normal — clustered, retained',
      c: '--done',
      t: 1,
      d: 'Candidates with outcome not_threat.',
    },
    // 04 Classify LLM/RAG
    thr: { col: 3, v: c.threats, label: 'Threats', c: '--crit', d: 'Candidates with outcome threat.' },
    fp: {
      col: 3,
      v: c.falsePositives,
      label: 'False positives — suppressed with evidence',
      c: '--acc',
      t: 1,
      d: 'Candidates with outcome false_positive (legacy status anomaly maps here).',
    },
    // 05 Consolidate
    camp: {
      col: 4,
      v: c.inCampaign,
      label: 'In campaigns',
      c: '--crit',
      d: `Threats grouped into ${c.campaigns} open campaign${c.campaigns === 1 ? '' : 's'} (two or more incidents each). Select to open the campaigns.`,
    },
  };
  if (c.escalated > 0) {
    nodes.esc = {
      col: 3,
      v: c.escalated,
      label: 'Escalated',
      c: '--need',
      t: 1,
      d: 'isThreat with confidence below handler policy threshold.',
    };
  }
  if (c.standalone > 0) {
    nodes.standalone = {
      col: 4,
      v: c.standalone,
      label: 'Standalone',
      c: '--acc-t',
      d: 'Threats that are not in a campaign: each is a campaign of one for the screen that opens it.',
    };
  }
  // 06 Plan
  if (c.remediations > 0) {
    nodes.rem = {
      col: 5,
      v: c.remediations,
      label: 'Remediations',
      c: '--prog',
      d: 'Remediation documents linked to threat events (arbitium_remediations).',
    };
  }
  // 02 Dedup also holds the candidates that have not been through it: still being analysed, or failed.
  if (c.analyzing > 0) {
    nodes.analyzing = {
      col: 1,
      v: c.analyzing,
      label: 'Analyzing',
      c: '--acc-f',
      t: 1,
      d: 'Candidates with analysis_status analyzing.',
    };
  }
  if (c.errors > 0) {
    nodes.error = {
      col: 1,
      v: c.errors,
      label: 'Errors',
      c: '--crit',
      t: 1,
      d: 'Candidates with analysis_status error.',
    };
  }
  if (c.pending > 0) {
    nodes.pending = {
      col: 1,
      v: c.pending,
      label: 'Pending',
      c: '--need',
      t: 1,
      d: 'Candidates with analysis_status pending.',
    };
  }
  if (c.duplicated > 0) {
    nodes.dup = {
      col: 1,
      v: c.duplicated,
      label: 'Duplicated',
      c: '--t3',
      t: 1,
      d: 'Candidates with outcome duplicated (dedup skip / match).',
    };
  }
  // 07 Execute + Validate: where each remediation stands.
  const statuses: [string, number, string, string, string][] = [
    ['remCompleted', c.remCompleted, 'Completed', '--done', 'Remediations with status completed.'],
    ['remInProgress', c.remInProgress, 'In progress', '--prog', 'Remediations with status in_progress.'],
    ['remPending', c.remPending, 'Awaiting decision', '--need', 'Remediations with status pending.'],
    ['remConfirmed', c.remConfirmed, 'Confirmed', '--acc-f', 'Remediations with status confirmed.'],
    ['remCancelled', c.remCancelled, 'Cancelled', '--t3', 'Remediations with status cancelled.'],
    [
      'remReverted',
      c.remReverted,
      'Reverted',
      '--low',
      'Remediations with status reverted: executed, then rolled back.',
    ],
  ];
  statuses.forEach(([key, v, label, token, d]) => {
    if (v > 0) nodes[key] = { col: 6, v, label, c: token, t: 1, d };
  });
  // A node that would be zero has no column to draw in.
  if (c.unique <= 0) delete nodes.unique;
  if (c.anomalies <= 0) delete nodes.ano;
  if (c.baseline <= 0) delete nodes.base;
  if (c.threats <= 0) delete nodes.thr;
  if (c.falsePositives <= 0) delete nodes.fp;
  if (c.inCampaign <= 0) delete nodes.camp;
  return nodes;
}

export function buildAlertFlowFlows(c: AlertFlowCounts): AlertFlow[] {
  const flows: AlertFlow[] = [];
  // Ingest → Dedup (and what stays behind): everything that was registered ends in one of these.
  if (c.unique > 0) flows.push(['raw', 'unique', c.unique]);
  if (c.duplicated > 0) flows.push(['raw', 'dup', c.duplicated]);
  if (c.analyzing > 0) flows.push(['raw', 'analyzing', c.analyzing]);
  if (c.errors > 0) flows.push(['raw', 'error', c.errors]);
  if (c.pending > 0) flows.push(['raw', 'pending', c.pending]);
  // Dedup → Cluster + Score → Classify
  if (c.anomalies > 0) flows.push(['unique', 'ano', c.anomalies]);
  if (c.baseline > 0) flows.push(['unique', 'base', c.baseline]);
  if (c.threats > 0) flows.push(['ano', 'thr', c.threats]);
  if (c.falsePositives > 0) flows.push(['ano', 'fp', c.falsePositives]);
  if (c.escalated > 0) flows.push(['ano', 'esc', c.escalated]);
  // Classify → Consolidate: every threat is in a campaign or stands alone, so the two add up to the threats.
  if (c.inCampaign > 0) flows.push(['thr', 'camp', c.inCampaign]);
  if (c.standalone > 0) flows.push(['thr', 'standalone', c.standalone]);
  // Consolidate → Plan: each threat has one plan.
  if (c.remediations > 0) {
    if (c.inCampaign > 0) flows.push(['camp', 'rem', c.inCampaign]);
    if (c.standalone > 0) flows.push(['standalone', 'rem', c.standalone]);
  }
  // Plan → Execute + Validate
  const branches: Array<[string, number]> = [
    ['remCompleted', c.remCompleted],
    ['remInProgress', c.remInProgress],
    ['remConfirmed', c.remConfirmed],
    ['remCancelled', c.remCancelled],
    ['remReverted', c.remReverted],
    ['remPending', c.remPending],
  ];
  branches
    .filter(([, v]) => v > 0)
    .sort((a, b) => {
      if (a[0] === 'remPending') return 1;
      if (b[0] === 'remPending') return -1;
      return b[1] - a[1];
    })
    .forEach(([key, v]) => flows.push(['rem', key, v]));
  return flows;
}

/**
 * How a node is drawn: on the main path (it goes on to the next stage), held back (it stops at its stage), waiting on a
 * person, failed, done, or still running. The colour follows it (DESIGN.md colour rules), so a reader learns one meaning
 * per colour: indigo goes on, slate stays behind, honey waits, red failed, green is done.
 */
export type AlertFlowKind = 'main' | 'held' | 'need' | 'bad' | 'done' | 'run';

const NODE_KIND: Record<string, AlertFlowKind> = {
  raw: 'main',
  unique: 'main',
  ano: 'main',
  thr: 'main',
  camp: 'main',
  standalone: 'main',
  rem: 'main',
  dup: 'held',
  analyzing: 'held',
  pending: 'held',
  base: 'held',
  fp: 'held',
  remCancelled: 'held',
  remReverted: 'held',
  error: 'bad',
  esc: 'need',
  remPending: 'need',
  remCompleted: 'done',
  remInProgress: 'run',
  remConfirmed: 'run',
};

export const alertFlowKind = (key: string): AlertFlowKind => NODE_KIND[key] ?? 'held';

/** Top-to-bottom order of the nodes inside a column: what goes on first, then what stays behind; what waits, last. */
export const ALERT_FLOW_ORDER: Record<number, string[]> = {
  1: ['unique', 'dup', 'analyzing', 'error', 'pending'],
  2: ['ano', 'base'],
  3: ['thr', 'esc', 'fp'],
  4: ['camp', 'standalone'],
  6: ['remCompleted', 'remInProgress', 'remConfirmed', 'remReverted', 'remCancelled', 'remPending'],
};

export interface AlertFlowCheck {
  /** The stage whose parts are counted against what came into it. */
  stage: string;
  parts: number;
  input: number;
  ok: boolean;
}

/**
 * Does each stage add up to what came into it? One line per stage that divides its input; the header shows one check
 * ("Every count adds up") and names the stages that do not.
 */
export function alertFlowChecks(c: AlertFlowCounts): AlertFlowCheck[] {
  const rows: [string, number, number][] = [
    ['Dedup', c.unique + c.duplicated + c.analyzing + c.errors + c.pending, c.raw],
    ['Cluster + Score', c.anomalies + c.baseline, c.unique],
    ['Classify LLM/RAG', c.threats + c.falsePositives + c.escalated, c.anomalies],
    ['Consolidate', c.inCampaign + c.standalone, c.threats],
    [
      'Execute + Validate',
      c.remCompleted + c.remInProgress + c.remPending + c.remConfirmed + c.remCancelled + c.remReverted,
      c.remediations,
    ],
  ];
  return rows.map(([stage, parts, input]) => ({ stage, parts, input, ok: parts === input }));
}
